import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { AlertNavigationLabel, AlertNavigationType, AlertStatus, getLastAlertDetail, getNavigationStatus } from "../../utils/alertUtils";
import { AlertSerie } from "../../types/alertSerie";
import { Range } from "../../utils/range";
import { useStores } from "../../context/storesContext";
import AlertGroupHeader from "../../components/AlertSectionHeader";
import AlertCard from "../../components/AlertCard";
import { CText } from "@carrefour/design-system-components-react";
import ErrorOrLoadingScreen from "../../components/ErrorOrLoadingScreen";
import AlertSidePanel from "../../components/AlertSidePanel/AlertSidePanel";
import AlertHeader from "../../components/AlertHeader/AlertHeader";
import { useAuth } from "../../Auth/AuthProvider";
import AlertNavigation from "../../components/AlertNavigation";
import { isEmailInWhitelist } from "../../utils/whiteListUtils";
import { fetchAlertsWithStoreKey } from "../../utils/fetcher";

const Page = styled.div`
  display: flex;
  flex-direction: row;
  @media (min-width: 768px) {
    gap: 1rem;
  }
  width: 100%;
  height: 100%;
`;

const Container = styled.div<{ flex?: number }>`
  background-color: white;
  height: 85vh;
  @media (max-height: 850px) {
    height: 80vh;
  }
  overflow-y: auto;
  flex: ${(props) => props.flex};
  min-width: 0;
  border-radius: 1rem;
`;

const HeaderAndSections = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const SectionsScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: left;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 15px;
`;

const CTextCustom = styled(CText)`
  font-size: .9rem !important;
  display: inline;
  font-family: system-ui;
  text-align: left;
  font-weight: bold;
`;

const Alert: React.FC = () => {

  const { selectedStore } = useStores();
  const [refreshPage, setRefresh] = useState(false);
  const [alertSeries, setAlertSeries] = useState<AlertSerie[] | undefined>(undefined);
  const [alertsSerieList, setAlertsSerieList] = useState<AlertSerie[] | undefined>(undefined);
  const [totalImpactFinancier, setTotalImpactFinancier] = useState<number>(0);
  const [groupName, setGroupName] = useState<string>('');
  const [groupIcon, setgroupIcon] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [batchLastExecutionDateTime, setBatchLastExecutionDateTime] = useState<string | undefined>(undefined);
  const [showAlertsView, setShowAlertsView] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!selectedStore?.storeKey) return;
    setIsLoading(true);
    setTotalImpactFinancier(0);
    setGroupName('');
    setgroupIcon('');
    setShowAlertsView(false);
    setShowBanner(false);

    fetchAlertsWithStoreKey(`/alerts?range=${Range.CurrentMonth}`, selectedStore.storeKey as string)
      .then(({ data, headers }) => {
        setAlertSeries(data);
        setAlertsSerieList(data);
        setBatchLastExecutionDateTime(headers.get("X-Batch-Execution-Time") ?? undefined);
      })
      .catch(() => setAlertSeries(undefined))
      .finally(() => setIsLoading(false));
  }, [selectedStore?.storeKey, refreshPage]);
  
  // Initialize isMobile with actual window width
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      const initialValue = window.innerWidth <= 768;
      return initialValue;
    }
    return false;
  });

  // Track isMobile changes
  useEffect(() => {
    const checkIsMobile = () => {
      const newIsMobile = window.innerWidth <= 768;
      
      // Always update - React will handle optimization
      setIsMobile(newIsMobile);
    };
        
    window.addEventListener('resize', checkIsMobile);
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []); // Empty dependency array

  // Calculate flex values based on mobile state and navigation
  const { container1Flex, container2Flex } = useMemo(() => {
    if (isMobile) {
      if (showAlertsView) {
        return { container1Flex: 0, container2Flex: 1 }; // Show alerts only
      } else {
        return { container1Flex: 1, container2Flex: 0 }; // Show sidebar only
      }
    } else {
      return { container1Flex: 1, container2Flex: 3 }; // Desktop view
    }
  }, [isMobile, showAlertsView]);

  const goToAlerts = () => {
    if(isMobile) {
      setShowAlertsView(true);
    }
  };

  const backToAlertMenu = () => {
    if(isMobile) {
      setShowAlertsView(false);
    }
  };

  const actionsLengths = useMemo(() => alertSeries?.filter(
    (serie) =>
      serie.status === AlertStatus.NEW ||
      serie.status === AlertStatus.IGNORED ||
      serie.status === AlertStatus.TOADJUST
  ).length, [alertSeries]);

  const createEmptyGroupedAlerts = (): Record<AlertNavigationType, AlertSerie[]> => ({
    [AlertNavigationType.TOPROCESS]: [],
    [AlertNavigationType.INPROGRESS]: [],
    [AlertNavigationType.RESOLVED]: [],
  });

  const groupedAlerts = useMemo(() => {
    const newGroupedAlerts = createEmptyGroupedAlerts();
    if (!alertsSerieList) return newGroupedAlerts;

    alertsSerieList.forEach((serie) => {
      const navigationType: AlertNavigationType = getNavigationStatus(serie.status);
      newGroupedAlerts[navigationType].push(serie);
    });

    // Sort TOPROCESS by impact
    newGroupedAlerts[AlertNavigationType.TOPROCESS].sort((a, b) => {
      const valA = a.impactFinancier ?? 0;
      const valB = b.impactFinancier ?? 0;
      return valB - valA;
    });

    return newGroupedAlerts;
  }, [alertsSerieList]);

  const groupRefChange = (groupName: string, totalImpact: number, groupIcon: string) => {
    setTotalImpactFinancier(totalImpact);
    setGroupName(groupName);
    setgroupIcon(groupIcon);
    setShowBanner((alertSeries?.length ?? 0) > 0 && totalImpact === 0);
    setAlertsSerieList(
      alertSeries?.filter((serie) => 
        (serie.alertDetails?.at(0)?.kpiGroupName || "") === groupName
      )
    );
  };
  
  const handleRefChange = (tab: string) => {
    if (!alertSeries) return;
    
    if (tab === "ALL") {
      setAlertsSerieList(alertSeries);
    } else if (tab === "ACTIONS") {
      setAlertsSerieList(
        alertSeries.filter(
          (serie) =>
            serie.status === AlertStatus.NEW ||
            serie.status === AlertStatus.IGNORED ||
            serie.status === AlertStatus.TOADJUST
        )
      );
    }
  };

  const renderAlertSections = () => (
    <>
      <SectionContainer key={AlertNavigationType.TOPROCESS}>
        <AlertGroupHeader
          status={AlertNavigationType.TOPROCESS}
          count={groupedAlerts[AlertNavigationType.TOPROCESS].length}
          label={AlertNavigationLabel[AlertNavigationType.TOPROCESS]}
        />
        {groupedAlerts[AlertNavigationType.TOPROCESS].length > 0 ? (
          groupedAlerts[AlertNavigationType.TOPROCESS].map((alertSerie: AlertSerie) => (
            <AlertCard
              key={alertSerie.idAlertSeries}
              alert={getLastAlertDetail(alertSerie)}
              alertSeriesId={alertSerie.idAlertSeries}
              startDate={alertSerie.startDate}
              status={alertSerie.status}
              dashboardUrl={alertSerie.dashboardUrl}
              impactFinancier={alertSerie.impactFinancier ?? 0}
              actionPlanSubmitted={() => setRefresh(!refreshPage)}
            />
          ))
        ) : (
          <CTextCustom>Vous n'avez aucune alerte sur notre analyse du mois précédent</CTextCustom>
        )}
      </SectionContainer>

      <SectionContainer key={AlertNavigationType.INPROGRESS}>
        <AlertGroupHeader
          status={AlertNavigationType.INPROGRESS}
          count={groupedAlerts[AlertNavigationType.INPROGRESS].length}
          label={AlertNavigationLabel[AlertNavigationType.INPROGRESS]}
        />
        {groupedAlerts[AlertNavigationType.INPROGRESS].length > 0 ? (
          groupedAlerts[AlertNavigationType.INPROGRESS].map((alertSerie: AlertSerie) => (
            <AlertCard
              key={alertSerie.idAlertSeries}
              alert={getLastAlertDetail(alertSerie)}
              alertSeriesId={alertSerie.idAlertSeries}
              startDate={alertSerie.startDate}
              deadlineDate={alertSerie.deadlineDate}
              status={alertSerie.status}
              dashboardUrl={alertSerie.dashboardUrl}
              nbActions={alertSerie.actionPlans?.length}
              actionPlanSubmitted={() => setRefresh(!refreshPage)}
            />
          ))
        ) : (
          <CTextCustom>Vous n'avez aucune alerte en cours</CTextCustom>
        )}
      </SectionContainer>

      <SectionContainer key={AlertNavigationType.RESOLVED}>
        <AlertGroupHeader
          status={AlertNavigationType.RESOLVED}
          count={groupedAlerts[AlertNavigationType.RESOLVED].length}
          label={AlertNavigationLabel[AlertNavigationType.RESOLVED]}
        />
        {groupedAlerts[AlertNavigationType.RESOLVED].length > 0 ? (
          groupedAlerts[AlertNavigationType.RESOLVED].map((alertSerie: AlertSerie) => (
            <AlertCard
              key={alertSerie.idAlertSeries}
              alert={getLastAlertDetail(alertSerie)}
              alertSeriesId={alertSerie.idAlertSeries}
              startDate={alertSerie.startDate}
              endDate={alertSerie.endDate}
              deadlineDate={alertSerie.deadlineDate}
              status={alertSerie.status}
              nbActions={alertSerie.actionPlans?.length}
              actionPlanSubmitted={() => setRefresh(!refreshPage)}
            />
          ))
        ) : (
          <CTextCustom>Vous n'avez aucune alerte résolue</CTextCustom>
        )}
      </SectionContainer>
    </>
  );

  return (
    <>
      {isLoading ? 
      <ErrorOrLoadingScreen isUnavailable={isLoading} size={"big"} messageError="Chargement des données..." /> : 
       isEmailInWhitelist("ALERT_FLIPPING_LIST", user) ? (
        <Page>
            <Container className="1" flex={container1Flex}>
              <AlertSidePanel alertSeries={alertSeries} groupRefChange={groupRefChange} goToAlers={goToAlerts} importedDate={batchLastExecutionDateTime ?? ""} />
            </Container>
            
            <Container className="2" flex={container2Flex}>
            <HeaderAndSections>
                <AlertHeader backToAlertMenu={backToAlertMenu} impactValue={totalImpactFinancier} isMobile={isMobile} groupIcon={groupIcon} groupName={groupName} showBanner={showBanner} />
              <SectionsScroll>
                  {renderAlertSections()}
              </SectionsScroll>
            </HeaderAndSections>
            </Container>
        </Page>
    )
    :
    (
      <>
        <AlertNavigation actionsLength={actionsLengths} importedDate={batchLastExecutionDateTime} refChanged={handleRefChange} />
        <Container>
          {renderAlertSections()}
        </Container>
      </>
    )}
    </>
  );
}

export default Alert;